/**
 * "Notify me on this computer, even with Tributary closed" (086). What it
 * notifies about is chosen above it (NotifySettings.tsx).
 *
 * At the foot of the bell, because that is where somebody looking at "work
 * handed to you" will think to ask for it. Per BROWSER, and it says so: a
 * person who switches it on at their desk has not switched it on on their
 * laptop.
 *
 * SAYS WHETHER IT ARRIVED. When a notification does not appear there are two
 * very different reasons -- it never reached the browser, or it did and
 * Windows hid it -- and the person cannot tell them apart. So the panel
 * shows when one was last handed to this browser, and "Send a test" checks
 * again a few seconds later and says which it was.
 *
 * Says nothing at all where it cannot work -- a browser without the Push API,
 * or a deployment without keys -- rather than offering a switch that fails.
 * Blocked is different: the person did that, and only they can undo it, in
 * the browser's own settings, so it says where.
 */
import { useEffect, useState } from 'react';
import { BellRing } from 'lucide-react';
import { ApiError, api } from '../lib/api.js';
import { disablePush, enablePush, lastDelivered, pushState, type PushState } from '../lib/push.js';
import { Button, useToast } from '../ui/index.js';

/** How long after "Send a test" to look for the delivery. The worker polls every few seconds. */
const CHECK_AFTER_MS = 10_000;

function at(iso: string): string {
  const d = new Date(iso);
  const today = d.toDateString() === new Date().toDateString();
  return today
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

export function PushSwitch({ tenant }: { tenant: string }) {
  const toast = useToast();
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [delivered, setDelivered] = useState<string | null>(null);
  /** After a test: did it reach the browser? */
  const [verdict, setVerdict] = useState<'checking' | 'reached' | 'not_reached' | null>(null);

  const refresh = async () => {
    const s = await pushState();
    setState(s);
    setDelivered(s === 'on' ? await lastDelivered().catch(() => null) : null);
    return s;
  };

  useEffect(() => {
    let live = true;
    pushState()
      .then(async (s) => {
        if (!live) return;
        setState(s);
        if (s === 'on') setDelivered(await lastDelivered().catch(() => null));
      })
      .catch(() => live && setState('unavailable'));
    return () => {
      live = false;
    };
  }, []);

  if (state === null || state === 'unsupported' || state === 'unavailable') return null;

  const sendTest = async () => {
    const before = delivered;
    setVerdict('checking');
    try {
      await api.post(`/api/v1/tenants/${tenant}/push/test`, {});
    } catch (e) {
      setVerdict(null);
      toast.error('Test not sent', e instanceof ApiError ? e.message : undefined);
      return;
    }
    window.setTimeout(() => {
      void lastDelivered()
        .catch(() => null)
        .then((now) => {
          setDelivered(now);
          setVerdict(now && now !== before ? 'reached' : 'not_reached');
        });
    }, CHECK_AFTER_MS);
  };

  const turnOn = async () => {
    setBusy(true);
    try {
      const s = await enablePush();
      setState(s);
      if (s === 'on') {
        toast.success('Notifications on', 'This browser will tell you when you are given a ticket, even with Tributary closed.');
        await sendTest();
      } else if (s === 'blocked') {
        toast.error('Notifications are blocked', 'Allow them for this site in your browser’s settings, then try again.');
      }
    } catch (e) {
      toast.error('Could not switch notifications on', e instanceof ApiError ? e.message : e instanceof Error ? e.message : undefined);
      void refresh();
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      setState(await disablePush());
      setDelivered(null);
      setVerdict(null);
      toast.success('Notifications off', 'This browser will not be told about tickets.');
    } catch (e) {
      toast.error('Could not switch notifications off', e instanceof Error ? e.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 border-t border-line-subtle px-3 py-2.5 text-2xs">
      <div className="flex items-start gap-2">
        <BellRing aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-tertiary" />
        <div className="min-w-0 flex-1">
          {state === 'on' && <p className="text-ink-secondary">This browser notifies you when you are given a ticket, even with Tributary closed.</p>}
          {state === 'off' && <p className="text-ink-secondary">Get a notification on this computer when you are given a ticket, even with Tributary closed.</p>}
          {state === 'blocked' && (
            <p className="text-ink-secondary">
              Notifications are blocked for this site. Allow them in your browser’s site settings (the padlock by the address), then come back here.
            </p>
          )}
        </div>
        {state === 'on' && (
          <Button size="sm" variant="ghost" onClick={turnOff} disabled={busy}>
            Turn off
          </Button>
        )}
        {state === 'off' && (
          <Button size="sm" variant="secondary" onClick={turnOn} disabled={busy}>
            Turn on
          </Button>
        )}
      </div>

      {state === 'on' && (
        <>
          <div className="flex items-center gap-2 text-ink-tertiary">
            <span className="min-w-0 flex-1">
              {verdict === 'checking'
                ? 'Sent a test. Checking it reached this browser…'
                : delivered
                  ? `Last delivered to this browser ${at(delivered)}.`
                  : 'Nothing delivered to this browser yet.'}
            </span>
            <Button size="sm" variant="ghost" onClick={sendTest} disabled={verdict === 'checking'}>
              Send a test
            </Button>
          </div>
          {verdict === 'reached' && (
            <p className="rounded-md bg-surface-sunken px-2 py-1.5 text-ink-secondary">
              It reached this browser. If nothing popped up, Windows is hiding it: open Settings, System, Notifications, turn
              notifications on for this browser, and turn off Do not disturb.
            </p>
          )}
          {verdict === 'not_reached' && (
            <p className="rounded-md bg-warn-tint px-2 py-1.5 text-ink-secondary">
              It has not reached this browser. Turn notifications off and on again here; if it still does not arrive, tell
              the desk administrator the time you tried.
            </p>
          )}
        </>
      )}
    </div>
  );
}
